import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { userService } from '../../services/userService';
import { noticeService } from '../../services/noticeService';
import { adverService } from '../../services/adverService';

// 최근 N일 날짜별 가입 수 집계
function buildJoinTrend(users: { createdDt: string }[], days: number) {
  const today = new Date();
  const result: { date: string; count: number }[] = [];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const label = `${d.getMonth() + 1}/${d.getDate()}`;
    const key = d.toISOString().slice(0, 10);
    const count = users.filter((u) => u.createdDt.slice(0, 10) === key).length;
    result.push({ date: label, count });
  }
  return result;
}

export default function Dashboard() {
  const { data: users = [], isLoading: usersLoading } = useQuery({ queryKey: ['users'], queryFn: userService.getAllUsers });
  const { data: notices = [], isLoading: noticesLoading } = useQuery({ queryKey: ['notices'], queryFn: noticeService.getAllNotices });
  const { data: advers = [], isLoading: adversLoading } = useQuery({ queryKey: ['advers'], queryFn: adverService.getAllAdvers });

  const isLoading = usersLoading || noticesLoading || adversLoading;

  // 회원 통계
  const totalUsers = users.length;
  const activeUsers = users.filter((u) => !u.isBlacklist && !u.isBlacklistPhone).length;
  const blacklistedUsers = users.filter((u) => u.isBlacklist || u.isBlacklistPhone).length;
  const premiumUsers = users.filter((u) => u.isAddFunction).length;
  const v1Users = users.filter((u) => u.deviceVersion === 'V1').length;
  const v2Users = users.filter((u) => u.deviceVersion === 'V2').length;

  // 공지 통계
  const totalNotices = notices.length;
  const visibleNotices = notices.filter((n) => n.isVisible).length;
  const pinnedNotices = notices.filter((n) => n.isPinned).length;

  // 광고 통계
  const totalAdvers = advers.length;
  const activeAdvers = advers.filter((a) => a.isVisible).length;
  const bannerAdvers = advers.filter((a) => a.adType === 'BANNER').length;
  const boxAdvers = advers.filter((a) => a.adType === 'BOX').length;

  // 신규 가입 추이 (최근 30일)
  const joinTrend = buildJoinTrend(users, 30);
  const todayJoins = joinTrend[joinTrend.length - 1]?.count ?? 0;
  const yesterdayJoins = joinTrend[joinTrend.length - 2]?.count ?? 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <i className="fas fa-circle-notch fa-spin text-3xl text-primary" />
          <p className="text-sm text-gray-300">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-600">대시보드</h1>
        <p className="text-gray-400 text-sm mt-0.5">배링 시스템 현황</p>
      </div>

      {/* KPI 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3">
        <KpiCard label="전체 회원" value={totalUsers} unit="명" icon="fas fa-users" iconBg="bg-primary/10" iconColor="text-primary" to="/admin/member" />
        <KpiCard label="활성 회원" value={activeUsers} unit="명" icon="fas fa-user-check" iconBg="bg-primary-mint/10" iconColor="text-primary-mint" to="/admin/member" />
        <KpiCard label="블랙리스트" value={blacklistedUsers} unit="명" icon="fas fa-user-slash" iconBg="bg-text-red/10" iconColor="text-text-red" to="/admin/member" highlight={blacklistedUsers > 0} />
        <KpiCard label="V1 기기" value={v1Users} unit="명" icon="fas fa-mobile-alt" iconBg="bg-marker-blue/10" iconColor="text-marker-blue" to="/admin/member" />
        <KpiCard label="V2 기기" value={v2Users} unit="명" icon="fas fa-mobile-alt" iconBg="bg-marker-purple/10" iconColor="text-marker-purple" to="/admin/member" />
        <KpiCard label="공개 공지" value={visibleNotices} unit="건" icon="fas fa-bell" iconBg="bg-primary-mint/10" iconColor="text-primary-mint" to="/admin/notice" />
        <KpiCard label="노출 광고" value={activeAdvers} unit="개" icon="fas fa-bullhorn" iconBg="bg-marker-orange/10" iconColor="text-marker-orange" to="/admin/adver" />
      </div>

      {/* 신규 가입 추이 + 구성 비율 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* 신규 가입 그래프 — 2/3 */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <i className="fas fa-user-plus text-sm text-primary" />
                <h2 className="text-sm font-bold text-gray-600">신규 가입 추이</h2>
              </div>
              <p className="text-xs text-gray-300 mt-0.5">최근 30일</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-600 tabular-nums">{todayJoins}<span className="text-xs font-normal text-gray-300 ml-1">명</span></p>
              <p className="text-xs text-gray-300">오늘 가입</p>
              {todayJoins !== yesterdayJoins && (
                <p className={`text-xs mt-0.5 font-medium ${todayJoins > yesterdayJoins ? 'text-primary' : 'text-text-red'}`}>
                  <i className={`fas fa-arrow-${todayJoins > yesterdayJoins ? 'up' : 'down'} mr-0.5`} />
                  전일 대비 {Math.abs(todayJoins - yesterdayJoins)}명
                </p>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={joinTrend} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <defs>
                <linearGradient id="joinGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6EB870" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#6EB870" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#AAAAAA' }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#AAAAAA' }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #EEEEEE', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                itemStyle={{ color: '#6EB870' }}
                formatter={(v) => [`${v ?? 0}명`, '신규 가입']}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#6EB870"
                strokeWidth={2}
                fill="url(#joinGradient)"
                dot={false}
                activeDot={{ r: 4, fill: '#6EB870' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* 회원 구성 비율 — 1/3 */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <i className="fas fa-chart-pie text-sm text-gray-400" />
              <h2 className="text-sm font-bold text-gray-600">회원 구성</h2>
            </div>
            <Link to="/admin/member" className="text-xs text-gray-300 hover:text-gray-500 transition-colors flex items-center gap-1">
              전체 보기 <i className="fas fa-arrow-right" />
            </Link>
          </div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-3xl font-bold text-gray-600 tabular-nums">{totalUsers.toLocaleString()}</p>
            <p className="text-xs text-gray-300">전체 회원</p>
          </div>
          <div className="space-y-2.5">
            <RatioBar label="활성" value={activeUsers} total={totalUsers} colorClass="bg-primary" />
            <RatioBar label="프리미엄" value={premiumUsers} total={totalUsers} colorClass="bg-marker-orange" />
            <RatioBar label="블랙리스트" value={blacklistedUsers} total={totalUsers} colorClass="bg-text-red" />
            <RatioBar label="V1" value={v1Users} total={totalUsers} colorClass="bg-marker-blue" />
            <RatioBar label="V2" value={v2Users} total={totalUsers} colorClass="bg-marker-purple" />
          </div>
        </div>
      </div>

      {/* 공지 + 광고 통계 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

        {/* 공지사항 */}
        <PanelCard title="공지사항 현황" icon="fas fa-bell" accentColor="text-primary-mint" linkTo="/admin/notice">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <StatRow label="전체" value={totalNotices} unit="건" />
            <StatRow label="공개" value={visibleNotices} unit="건" valueColor="text-primary" />
            <StatRow label="비공개" value={totalNotices - visibleNotices} unit="건" />
            <StatRow label="상단 고정" value={pinnedNotices} unit="건" valueColor="text-marker-orange" />
          </div>
          <div className="mt-4 space-y-2">
            <RatioBar label="공개" value={visibleNotices} total={totalNotices} colorClass="bg-primary-mint" />
            <RatioBar label="고정" value={pinnedNotices} total={totalNotices} colorClass="bg-marker-orange" />
          </div>
        </PanelCard>

        {/* 광고 */}
        <PanelCard title="광고 현황" icon="fas fa-bullhorn" accentColor="text-marker-orange" linkTo="/admin/adver">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <StatRow label="전체" value={totalAdvers} unit="개" />
            <StatRow label="노출 중" value={activeAdvers} unit="개" valueColor="text-primary" />
            <StatRow label="숨김" value={totalAdvers - activeAdvers} unit="개" />
            <StatRow label="배너" value={bannerAdvers} unit="개" valueColor="text-marker-blue" />
            <StatRow label="박스" value={boxAdvers} unit="개" valueColor="text-marker-purple" />
          </div>
          <div className="mt-4 space-y-2">
            <RatioBar label="노출" value={activeAdvers} total={totalAdvers} colorClass="bg-marker-orange" />
            <RatioBar label="배너" value={bannerAdvers} total={totalAdvers} colorClass="bg-marker-blue" />
          </div>
        </PanelCard>
      </div>

    </div>
  );
}

/* ── 컴포넌트 ── */

function KpiCard({ label, value, unit, icon, iconBg, iconColor, to, highlight = false }: {
  label: string; value: number; unit: string;
  icon: string; iconBg: string; iconColor: string;
  to: string; highlight?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`bg-white rounded-xl border shadow-sm p-3 hover:shadow-md transition-shadow flex flex-col gap-2 ${highlight ? 'border-text-red/30' : 'border-gray-100'}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400 leading-tight">{label}</p>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
          <i className={`${icon} text-xs ${iconColor}`} />
        </div>
      </div>
      <p className={`text-xl font-bold tabular-nums leading-none ${highlight && value > 0 ? 'text-text-red' : 'text-gray-600'}`}>
        {value.toLocaleString()}
        <span className="text-xs font-normal text-gray-300 ml-1">{unit}</span>
      </p>
    </Link>
  );
}

function PanelCard({ title, icon, accentColor, linkTo, children }: {
  title: string; icon: string; accentColor: string; linkTo: string; children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className={`${icon} text-sm ${accentColor}`} />
          <h2 className="text-sm font-bold text-gray-600">{title}</h2>
        </div>
        <Link to={linkTo} className="text-xs text-gray-300 hover:text-gray-500 transition-colors flex items-center gap-1">
          전체 보기 <i className="fas fa-arrow-right" />
        </Link>
      </div>
      {children}
    </div>
  );
}

function StatRow({ label, value, unit, valueColor = 'text-gray-600' }: {
  label: string; value: number; unit: string; valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-400">{label}</span>
      <span className={`text-sm font-semibold tabular-nums ${valueColor}`}>
        {value.toLocaleString()}<span className="text-xs font-normal text-gray-300 ml-0.5">{unit}</span>
      </span>
    </div>
  );
}

function RatioBar({ label, value, total, colorClass }: {
  label: string; value: number; total: number; colorClass: string;
}) {
  const pct = total === 0 ? 0 : Math.round((value / total) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span className="text-gray-300 tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
